import { Fragment, type ReactNode } from "react";
import { Folder, Info, X, XCircle } from "lucide-react";
import { PersonAvatar } from "~/components/PersonAvatar";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import { PreviewRow } from "~/pages/manifold-nextgen/components/PreviewRow";
import {
  DISPLAY_OPERATION,
  NumericOperation,
} from "~/pages/manifold-nextgen/components/cohorts/enums/filterOperation";
import {
  AccessRequestStatus,
  CohortAccessRequest,
  JsonValue,
} from "~/pages/manifold-nextgen/components/cohorts/interfaces/access_requests";
import { FilterQuery } from "~/pages/manifold-nextgen/components/cohorts/interfaces/criterion";
import { nextGenDatasetDetailPath } from "~/router/constants";
import { formatDateTime } from "~/utils/date";
import { ChildDatasetAccessRequestStatusBadge } from "./ChildDatasetAccessRequestStatusBadge";
import {
  APPROVE_BUTTON_CLASS,
  ChildDatasetApproveRequestModal,
} from "./ChildDatasetApproveRequestModal";
import { ChildDatasetDenyRequestModal } from "./ChildDatasetDenyRequestModal";
import { type ChildDatasetExportPanelState } from "./ChildDatasetExportPanel";

const { VITE_CATALOG_API } = import.meta.env;

interface DetailFieldProps {
  label: string;
  tooltip?: string;
  children: ReactNode;
}

function DetailField({ label, tooltip, children }: DetailFieldProps) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1">
        <p className="text-sm font-semibold uppercase text-muted-foreground">{label}</p>
        {tooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="size-3 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-64 break-words whitespace-normal">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <p className="text-sm text-foreground">{children}</p>
    </div>
  );
}

interface CustomFieldValueProps {
  value: JsonValue;
}

function CustomFieldValue({ value }: CustomFieldValueProps) {
  if (value === null || value === undefined) {
    return <span className="text-sm text-foreground">—</span>;
  }
  if (Array.isArray(value)) {
    if (value.every((item) => item === null || typeof item !== "object")) {
      return <span className="text-sm text-foreground">{value.map(String).join(", ")}</span>;
    }
    return (
      <div className="space-y-2">
        {value.map((item, index) => (
          <CustomFieldValue key={index} value={item} />
        ))}
      </div>
    );
  }
  if (typeof value === "object") {
    return (
      <div className="space-y-5">
        {Object.entries(value).map(([label, entryValue]) => (
          <div key={label} className="space-y-0.5">
            <p className="text-sm font-semibold uppercase text-muted-foreground">{label}</p>
            <CustomFieldValue value={entryValue} />
          </div>
        ))}
      </div>
    );
  }
  return <span className="text-sm text-foreground">{String(value)}</span>;
}

interface ClauseDividerProps {
  label: string;
  emphasized?: boolean;
}

function ClauseDivider({ label, emphasized }: ClauseDividerProps) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-border" />
      <span
        className={cn(
          "text-xs font-semibold text-muted-foreground",
          emphasized && "rounded-full border border-border bg-muted px-3 py-1"
        )}
      >
        {label}
      </span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

const formatFilterValues = (value: string | number | (string | number)[] | null): string[] => {
  if (value == null) return [];
  return (Array.isArray(value) ? value : [value]).map(String);
};

const formatFilterLabel = (filterId: string) => {
  const spaced = filterId.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const formatDomainLabel = (domainId: string) =>
  domainId
    .replace(/[_-]/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

// bg-red-700/text-red-700 etc. invert between light and dark themes, which washes out an
// outline action button. This references the primitive token directly so Deny keeps the same
// fixed color in both themes (matching the design system's button-outline-destructive spec).
const DENY_BUTTON_CLASS =
  "min-w-40 text-[var(--dt-primitives-color-red-600)] hover:bg-[var(--dt-primitives-color-red-75)]";

const isDependentSelectFilterQuery = (
  filterQuery: FilterQuery
): filterQuery is Extract<FilterQuery, { selected_option: string }> =>
  "selected_option" in filterQuery;

const VALUE_BADGE_LIMIT = 3;

function ValueBadges({ values, limit = VALUE_BADGE_LIMIT }: { values: string[]; limit?: number }) {
  const visible = values.slice(0, limit);
  const hidden = values.slice(limit);

  return (
    <>
      {visible.map((value) => (
        <Badge key={value} variant="muted" className="font-mono">
          {value}
        </Badge>
      ))}
      {hidden.length > 0 && <Badge variant="outline">+{hidden.length}</Badge>}
    </>
  );
}

function FilterQueryRow({ filterQuery }: { filterQuery: FilterQuery }) {
  const domainBadge = (
    <Badge variant="secondary" className="w-fit">
      {formatDomainLabel(filterQuery.domain_id)}
    </Badge>
  );

  if (isDependentSelectFilterQuery(filterQuery)) {
    const secondaryValues = formatFilterValues(filterQuery.secondary_selected_options);
    return (
      <div className="flex flex-col items-start gap-1">
        {domainBadge}
        <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <span className="text-foreground">{formatFilterLabel(filterQuery.filter_id)}</span>
          <Badge variant="muted" className="font-mono">
            {filterQuery.selected_option}
          </Badge>
          {secondaryValues.length > 0 && (
            <>
              <span className="text-muted-foreground">
                {DISPLAY_OPERATION[filterQuery.secondary_selected_operation]}
              </span>
              <ValueBadges values={secondaryValues} />
            </>
          )}
        </span>
      </div>
    );
  }

  const selectedOperation =
    "selected_operation" in filterQuery ? filterQuery.selected_operation : "";
  const values = formatFilterValues(
    "selected_options" in filterQuery ? filterQuery.selected_options : null
  );
  const isRange =
    selectedOperation === NumericOperation.IN_RANGE ||
    selectedOperation === NumericOperation.NOT_IN_RANGE;

  return (
    <div className="flex flex-col items-start gap-1">
      {domainBadge}
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <span className="text-foreground">{formatFilterLabel(filterQuery.filter_id)}</span>
        {selectedOperation && (
          <span className="text-muted-foreground">{DISPLAY_OPERATION[selectedOperation]}</span>
        )}
        {isRange && values.length === 2 ? (
          <>
            <Badge variant="muted" className="font-mono">
              {values[0]}
            </Badge>
            <span className="text-muted-foreground">and</span>
            <Badge variant="muted" className="font-mono">
              {values[1]}
            </Badge>
          </>
        ) : (
          <ValueBadges values={values} />
        )}
      </span>
    </div>
  );
}

interface ChildDatasetAccessRequestDetailsPanelProps {
  request: CohortAccessRequest | null;
  isPreview: boolean;
  onClose: () => void;
  confirmDeny: CohortAccessRequest | null;
  isDenying: boolean;
  denyReason: string;
  onDenyReasonChange: (value: string) => void;
  onDenyClick: (request: CohortAccessRequest) => void;
  onDenyConfirm: () => void;
  onDenyCancel: () => void;
  onApproveClick: (request: CohortAccessRequest) => void;
  exportPanel: ChildDatasetExportPanelState | null;
  onExportPanelDatasetNameChange: (name: string) => void;
  onExportPanelExport: () => void;
  onExportPanelApprove: () => void;
  onExportPanelCancel: () => void;
  isApproving: boolean;
  onCancelClick: () => void;
  isCancelling: boolean;
}

export function ChildDatasetAccessRequestDetailsPanel({
  request,
  isPreview,
  onClose,
  confirmDeny,
  isDenying,
  denyReason,
  onDenyReasonChange,
  onDenyClick,
  onDenyConfirm,
  onDenyCancel,
  onApproveClick,
  exportPanel,
  onExportPanelDatasetNameChange,
  onExportPanelExport,
  onExportPanelApprove,
  onExportPanelCancel,
  isApproving,
  onCancelClick,
  isCancelling,
}: ChildDatasetAccessRequestDetailsPanelProps) {
  const criteria = request?.criteria ?? [];
  const customFieldEntries = Object.entries(request?.custom_fields ?? {});
  const canApprove =
    !isPreview &&
    (request?.status === AccessRequestStatus.IN_REVIEW ||
      request?.status === AccessRequestStatus.EXPORT_FAILED ||
      request?.status === AccessRequestStatus.EXPORT_SUCCESSFUL);
  const canDeny =
    !isPreview &&
    (request?.status === AccessRequestStatus.PENDING ||
      request?.status === AccessRequestStatus.IN_REVIEW ||
      request?.status === AccessRequestStatus.EXPORT_FAILED ||
      request?.status === AccessRequestStatus.EXPORT_SUCCESSFUL);
  const canCancel = isPreview && request?.status === AccessRequestStatus.PENDING;

  const exportedDatasetId = request?.child_dataset_id ?? null;
  const { data: exportedDataset, error: exportedDatasetError } = useFetcher<{ title: string }>(
    exportedDatasetId ? `${VITE_CATALOG_API}/datasets/${exportedDatasetId}` : null
  );

  return (
    <div className="flex h-full flex-col gap-0 border-l border-border bg-background">
      <div className="print-area flex shrink-0 items-center justify-between border-b border-border p-4 pr-3">
        <h1 className="font-semibold text-foreground">Request Details</h1>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
          <X className="size-4" />
        </Button>
      </div>
      {request && (
        <div className="print-area flex-1 space-y-4 overflow-y-auto p-6 print:h-auto print:overflow-visible">
          <div className="flex items-center justify-between gap-1.5">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5">
                <ChildDatasetAccessRequestStatusBadge status={request.status} />
                {request.status === AccessRequestStatus.DENIED && request.status_reason && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <XCircle className="size-3.5 text-destructive" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-64 break-words whitespace-normal">
                      {request.status_reason}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className="text-xs font-semibold uppercase text-muted-foreground">
                Exported Dataset
              </span>
              {exportedDatasetId ? (
                exportedDatasetError ? (
                  <span className="text-sm italic text-muted-foreground">Dataset unavailable</span>
                ) : (
                  <PreviewRow
                    items={[
                      {
                        key: exportedDatasetId,
                        label: exportedDataset?.title ?? exportedDatasetId,
                        leading: (
                          <Folder className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                        ),
                        to: nextGenDatasetDetailPath(exportedDatasetId),
                      },
                    ]}
                  />
                )
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-lg bg-muted p-4">
            <PersonAvatar name={request.created_by} email={request.created_by} size={40} />
            <div>
              <p className="text-sm font-semibold text-foreground">{request.created_by}</p>
              <p className="text-xs text-muted-foreground">
                Requested {formatDateTime(request.created_at, { hour: "numeric" })}
              </p>
            </div>
          </div>

          <DetailField label="Intended use">{request.intended_use}</DetailField>

          <div className="space-y-5">
            <DetailField label="Cohort">{request.cohort_name}</DetailField>
            <DetailField
              label="Participants"
              tooltip="Participant counts reflect the count of participants in the dataset when this request was created — the counts are not updated as the underlying data changes."
            >
              {request.participant_count?.toLocaleString() ?? "—"}
            </DetailField>
            <DetailField label="Description">{request.cohort_description || "—"}</DetailField>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-semibold uppercase text-muted-foreground">Criteria</p>
            {criteria.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No criteria were recorded for this request.
              </p>
            ) : (
              <div className="space-y-4">
                {criteria.map((criterion, groupIndex) => {
                  return (
                    <Fragment key={criterion.id}>
                      {groupIndex > 0 && <ClauseDivider label="AND" emphasized />}
                      <div className="rounded-lg border border-border p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-foreground">
                            {criterion.description || `Criteria Group ${groupIndex + 1}`}
                          </p>
                          {criterion.participant_count != null && (
                            <span className="text-xs text-muted-foreground">
                              {criterion.participant_count.toLocaleString()} participants
                            </span>
                          )}
                        </div>
                        {!criterion.content || criterion.content.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No conditions.</p>
                        ) : (
                          <div className="space-y-2">
                            {criterion.content.map((conjunctiveClause, clauseIndex) => (
                              <Fragment key={clauseIndex}>
                                {clauseIndex > 0 && <ClauseDivider label="OR" />}
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                  {conjunctiveClause.map((filterQuery, filterIndex) => (
                                    <Fragment key={filterIndex}>
                                      {filterIndex > 0 && (
                                        <span className="text-xs font-semibold text-muted-foreground">
                                          AND
                                        </span>
                                      )}
                                      <FilterQueryRow filterQuery={filterQuery} />
                                    </Fragment>
                                  ))}
                                </div>
                              </Fragment>
                            ))}
                          </div>
                        )}
                      </div>
                    </Fragment>
                  );
                })}
              </div>
            )}
          </div>

          {customFieldEntries.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-semibold uppercase text-muted-foreground">
                Additional Details
              </p>
              {customFieldEntries.map(([header, value]) => (
                <div key={header} className="space-y-2">
                  <p className="text-sm font-semibold text-foreground">{header}</p>
                  <CustomFieldValue value={value} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {request && (canApprove || canDeny || canCancel) && (
        <div className="flex shrink-0 flex-col gap-4 border-t border-border p-4">
          <div className="flex flex-row justify-end gap-2">
            {canCancel && (
              <Button
                variant="outline"
                size="lg"
                className="min-w-40"
                loading={isCancelling}
                onClick={onCancelClick}
                disabled={isCancelling}
              >
                Cancel Request
              </Button>
            )}
            {canDeny && (
              <Button
                variant="outline"
                size="lg"
                className={DENY_BUTTON_CLASS}
                onClick={() => onDenyClick(request)}
              >
                Deny
              </Button>
            )}
            {canApprove && (
              <Button
                size="lg"
                className={APPROVE_BUTTON_CLASS}
                onClick={() => onApproveClick(request)}
              >
                Approve
              </Button>
            )}
          </div>
        </div>
      )}
      {request && (
        <ChildDatasetDenyRequestModal
          open={confirmDeny !== null}
          denyReason={denyReason}
          onDenyReasonChange={onDenyReasonChange}
          onConfirm={onDenyConfirm}
          onCancel={onDenyCancel}
          isDenying={isDenying}
        />
      )}
      {request && exportPanel && (
        <ChildDatasetApproveRequestModal
          open
          request={request}
          exportPanel={exportPanel}
          onDatasetNameChange={onExportPanelDatasetNameChange}
          onExport={onExportPanelExport}
          onApprove={onExportPanelApprove}
          onCancel={onExportPanelCancel}
          isApproving={isApproving}
        />
      )}
    </div>
  );
}
